import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  CartesianGrid,
} from 'recharts';
import { useMacroStore } from '@/store/macroStore';
import { useChartStore } from '@/store/chartStore';
import { api } from '@/services/api';
import {
  computeCCF,
  aggregateToMonthly,
  fmtLag,
  lagDescription,
  correlationStrength,
} from './macroMath';

interface CCFPanelProps {
  seriesId: string;
  color: string;
  onLagSelect?: (lag: number | null) => void;
}

const MIN_LAG = -6;
const MAX_LAG = 12;
const SIG_BAND = 0.3; // approx 95% CI for monthly data

interface BarPayload {
  lag: number;
  lagLabel: string;
  r: number | null;
}

/** Custom bar shape with fill logic and click handler. */
function CustomBarShape(props: {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  payload?: BarPayload;
  peakLag: number | null;
  selectedLag: number | null;
  color: string;
  onClick: (lag: number) => void;
}) {
  const { x = 0, y = 0, width = 0, height = 0, payload, peakLag, selectedLag, color, onClick } = props;
  const r = payload?.r ?? 0;
  const lag = payload?.lag ?? 0;
  const isPeak = lag === peakLag;
  const isSelected = lag === selectedLag;
  let fill: string;
  if (isPeak) {
    fill = color;
  } else if (r > SIG_BAND) {
    fill = '#639922';
  } else if (r < -SIG_BAND) {
    fill = '#A32D2D';
  } else {
    fill = '#6b7280'; // darker gray so it shows in both light & dark themes
  }

  // Ensure a minimum visible height (3 px) so tiny correlations don't disappear
  const minH = 3;
  const h = Math.max(minH, Math.abs(height));
  // Recharts passes `y` at the data value and `height` negative for bars below
  // the baseline. For negative bars we must start the rect at `y - h` so it
  // extends upward from the value back to the baseline.
  const barY = height >= 0 ? y + height - h : y - h;

  return (
    <rect
      x={x}
      y={barY}
      width={width}
      height={h}
      fill={fill}
      stroke={isSelected ? color : 'none'}
      strokeWidth={isSelected ? 2 : 0}
      rx={2}
      style={{ cursor: 'pointer' }}
      onClick={() => onClick(lag)}
    />
  );
}

/** Custom tooltip for CCF bars. */
function CCFTooltip({
  active,
  payload,
  indicatorName,
  symbolName,
}: {
  active?: boolean;
  payload?: Array<{ payload: BarPayload }>;
  indicatorName: string;
  symbolName: string;
}) {
  if (!active || !payload || !payload.length) return null;
  const item = payload[0].payload;
  const r = item.r ?? 0;
  const { label, color } = correlationStrength(r);
  const sig = Math.abs(r) > SIG_BAND;

  return (
    <div className="bg-panel border border-border rounded-lg px-3 py-2 text-xs shadow-lg max-w-[260px]">
      <p className="text-muted mb-1">
        Lag: {item.lag > 0 ? `+${item.lag}` : item.lag} months
      </p>
      <p className="font-medium" style={{ color }}>
        r = {r.toFixed(2)} ({label} {r >= 0 ? 'positive' : 'negative'})
      </p>
      <p className="text-muted mt-1">
        Significant: {sig ? 'Yes' : `No (below 95% CI ±${SIG_BAND})`}
      </p>
      <p className="text-[11px] text-text mt-1.5 leading-snug">
        {lagDescription(item.lag, indicatorName, symbolName)}
      </p>
    </div>
  );
}

export function CCFPanel({ seriesId, color, onLagSelect }: CCFPanelProps) {
  const seriesMeta = useMacroStore((s) => s.seriesMeta.find((m) => m.series_id === seriesId));
  const seriesData = useMacroStore((s) => s.seriesData[seriesId]);
  const symbol = useChartStore((s) => s.symbol);
  const timeframe = useChartStore((s) => s.timeframe);

  const [priceBars, setPriceBars] = useState<Array<{ time: number; close: number }>>([]);
  const [loading, setLoading] = useState(false);
  const [windowMonths, setWindowMonths] = useState(36);
  const [selectedLag, setSelectedLag] = useState<number | null>(null);

  // Fetch price data
  useEffect(() => {
    setLoading(true);
    api
      .ohlcv(symbol, timeframe, 500)
      .then((res) => setPriceBars(res.data.map((b) => ({ time: b.time, close: b.close }))))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [symbol, timeframe]);

  // Compute CCF
  const ccfData: (BarPayload & { r: number })[] = useMemo(() => {
    if (!seriesData || seriesData.length < 10 || priceBars.length < 10) return [];

    // Truncate macro data to selected window
    const truncatedMacro =
      windowMonths >= seriesData.length
        ? seriesData
        : seriesData.slice(-windowMonths);

    const monthlyPrices = aggregateToMonthly(priceBars);
    const results = computeCCF(truncatedMacro, monthlyPrices, MIN_LAG, MAX_LAG);

    return results
      .filter((r): r is { lag: number; r: number } => r.r !== null)
      .map((r) => ({
        lag: r.lag,
        lagLabel: fmtLag(r.lag),
        r: r.r,
      }));
  }, [seriesData, priceBars, windowMonths]);

  const peak = useMemo(() => {
    if (ccfData.length === 0) return null;
    return ccfData.reduce((max, curr) =>
      Math.abs(curr.r) > Math.abs(max.r) ? curr : max,
    );
  }, [ccfData]);

  const peakLag = peak?.lag ?? null;

  const handleBarClick = useCallback(
    (lag: number) => {
      setSelectedLag(lag);
      onLagSelect?.(lag);
    },
    [onLagSelect],
  );

  const indicatorName = seriesMeta?.name ?? seriesId;

  // Regime status
  const regimeStatus = useMemo(() => {
    if (!peak) return { label: 'Normal', color: '#22c55e' };
    const absPeak = Math.abs(peak.r);
    if (absPeak > 0.5) return { label: 'Normal', color: '#22c55e' };
    if (absPeak > 0.3) return { label: 'Weakening', color: '#f59e0b' };
    return { label: 'Break', color: '#ef4444' };
  }, [peak]);

  // Optimal lag text
  const optimalLagText = useMemo(() => {
    if (peakLag === null) return '—';
    if (peakLag === 0) return 'Coincident';
    if (peakLag > 0) return `Lead: ${peakLag} mo`;
    return `Lags ${symbol}: ${Math.abs(peakLag)} mo`;
  }, [peakLag, symbol]);

  return (
    <div className="flex gap-3 h-full min-h-[320px]">
      {/* ── Left strip (140px) ─────────────────────────────────────── */}
      <div className="w-[140px] shrink-0 flex flex-col gap-2">
        <label className="text-[10px] text-muted uppercase tracking-wider">Indicator</label>
        <div className="text-xs font-medium text-text truncate" title={indicatorName}>
          {indicatorName}
        </div>

        {peak && (
          <>
            <div className="mt-1">
              <p className="text-[10px] text-muted">Peak r</p>
              <span
                className="inline-flex items-center px-1.5 py-0.5 rounded text-[11px] font-semibold"
                style={{
                  background: peak.r > 0 ? 'rgba(99,153,34,0.15)' : 'rgba(163,45,45,0.15)',
                  color: peak.r > 0 ? '#639922' : '#A32D2D',
                }}
              >
                {peak.r > 0 ? '+' : ''}
                {peak.r.toFixed(2)}
              </span>
            </div>

            <div>
              <p className="text-[10px] text-muted">Optimal lag</p>
              <p className="text-xs font-medium text-text">{optimalLagText}</p>
            </div>
          </>
        )}

        <div className="mt-auto">
          <p className="text-[10px] text-muted mb-1">Window</p>
          <div className="flex flex-wrap gap-1">
            {[12, 24, 36, 60].map((m) => (
              <button
                key={m}
                onClick={() => setWindowMonths(m)}
                className={`px-1.5 py-0.5 rounded text-[10px] font-medium transition-colors ${
                  windowMonths === m
                    ? 'bg-accent text-white'
                    : 'bg-panel-bg text-muted hover:text-text'
                }`}
              >
                {m}m
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Center chart ───────────────────────────────────────────── */}
      <div className="flex-1 min-w-0">
        {loading || ccfData.length === 0 ? (
          <div className="h-full flex items-center justify-center text-xs text-muted">
            {loading ? 'Computing CCF…' : 'Insufficient data'}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={ccfData} margin={{ top: 28, right: 16, bottom: 8, left: 8 }} style={{ overflow: 'visible' }}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" opacity={0.3} />
              <XAxis
                dataKey="lagLabel"
                tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--color-border)' }}
              />
              <YAxis
                domain={[-1.25, 1.25]}
                ticks={[-1, -0.5, 0, 0.5, 1]}
                tick={{ fontSize: 10, fill: 'var(--color-muted)' }}
                tickLine={false}
                axisLine={{ stroke: 'var(--color-border)' }}
                width={36}
                padding={{ top: 10, bottom: 10 }}
              />
              <Tooltip
                content={<CCFTooltip indicatorName={indicatorName} symbolName={symbol} />}
                cursor={{ fill: 'rgba(128,128,128,0.08)' }}
              />
              <ReferenceLine y={0} stroke="var(--color-border)" strokeDasharray="4 4" />
              <ReferenceLine
                y={SIG_BAND}
                stroke="#639922"
                strokeDasharray="4 4"
                strokeOpacity={0.5}
                label={{ value: '95% CI', position: 'insideTopRight', fontSize: 9, fill: '#639922' }}
              />
              <ReferenceLine
                y={-SIG_BAND}
                stroke="#A32D2D"
                strokeDasharray="4 4"
                strokeOpacity={0.5}
                label={{ value: '95% CI', position: 'insideBottomRight', fontSize: 9, fill: '#A32D2D' }}
              />
              {peakLag !== null && (
                <ReferenceLine
                  x={fmtLag(peakLag)}
                  stroke={color}
                  strokeDasharray="4 4"
                  label={{
                    value: `peak r=${peak?.r.toFixed(2) ?? '—'} (${fmtLag(peakLag)}m)`,
                    position: 'insideTopLeft',
                    fontSize: 9,
                    fill: color,
                  }}
                />
              )}
              <ReferenceLine
                x="0"
                stroke="var(--color-border)"
                strokeDasharray="4 4"
              />
              <Bar
                dataKey="r"
                shape={(props: unknown) => (
                  <CustomBarShape
                    {...(props as Record<string, unknown>)}
                    peakLag={peakLag}
                    selectedLag={selectedLag}
                    color={color}
                    onClick={handleBarClick}
                  />
                )}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* ── Right strip (110px) ────────────────────────────────────── */}
      <div className="w-[110px] shrink-0 flex flex-col gap-2">
        <div className="bg-panel-bg rounded-lg px-2.5 py-2 border border-border">
          <p className="text-[10px] text-muted">Peak r</p>
          <p className="text-sm font-semibold text-text">
            {peak ? `${peak.r > 0 ? '+' : ''}${peak.r.toFixed(2)}` : '—'}
          </p>
        </div>
        <div className="bg-panel-bg rounded-lg px-2.5 py-2 border border-border">
          <p className="text-[10px] text-muted">Optimal lag</p>
          <p className="text-sm font-semibold text-text">{optimalLagText}</p>
        </div>
        <div className="bg-panel-bg rounded-lg px-2.5 py-2 border border-border">
          <p className="text-[10px] text-muted">Significant?</p>
          <p className="text-sm font-semibold" style={{ color: peak && Math.abs(peak.r) > SIG_BAND ? '#639922' : '#B4B2A9' }}>
            {peak ? (Math.abs(peak.r) > SIG_BAND ? 'Yes' : 'No') : '—'}
          </p>
        </div>

        <div className="mt-auto flex items-center gap-1.5 px-1">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ background: regimeStatus.color }}
          />
          <span className="text-[11px] font-medium" style={{ color: regimeStatus.color }}>
            {regimeStatus.label}
          </span>
        </div>
      </div>
    </div>
  );
}
